import { format } from "date-fns";
import { datosDeCore } from "./respuestaDeCore";

/**
 * Las cajas de escritorio del negocio, como las lista `GET /pos/terminals`
 * (§2.1 de OFFLINE-POS-RONDA-1.md): la presencia y los pendientes de cada una.
 * Las lee el dueño, y quien cierra la caja de un local (D16).
 */
export interface CajaDelNegocio {
  id: string;
  warehouseId: string;
  name: string;
  prefix: string;
  status: "active" | "deactivated";
  appVersion: string | null;
  lastHeartbeatAt: string | null;
  /** Lo que tenía sin subir en el último latido. */
  pendingCount: number;
  oldestPendingAt: string | null;
  lastSeqGenerated: number;
  lastSeqUploaded: number;
  seqGaps: number[];
  /**
   * El mandato de la caja (ronda 2, §2.4): `null` si no tiene. Un core de la
   * ronda 1 no manda el campo.
   */
  mandate?: {
    id: string;
    renewedAt: string;
    softUntil: string;
    hardUntil: string;
  } | null;
}

export function cajasDeLaRespuesta(cuerpo: unknown): CajaDelNegocio[] {
  const datos = datosDeCore(cuerpo);
  return Array.isArray(datos)
    ? (datos as CajaDelNegocio[]).filter(
        (caja) => typeof caja?.id === "string" && caja.id !== ""
      )
    : [];
}

const hora = (iso: string) => format(new Date(iso), "dd/MM HH:mm");

/** Un tramo con red se corta a los 90 s sin latido (§2.4 de la ronda 2). */
export const SIN_SENAL_MS = 90_000;

/**
 * El mandato vence a las 72 h del último latido que lo renovó (§2.3, D17): se
 * renueva con cada latido, así que su vencimiento sale del último.
 */
export const VIGENCIA_DEL_MANDATO_MS = 72 * 60 * 60 * 1000;

export type SenalDeLaCaja = "conectada" | "sin-senal" | "sin-latidos" | "baja";

export function senalDeLaCaja(
  caja: Pick<CajaDelNegocio, "status" | "lastHeartbeatAt">,
  ahora: number
): SenalDeLaCaja {
  if (caja.status !== "active") return "baja";
  const ultimo = caja.lastHeartbeatAt ? Date.parse(caja.lastHeartbeatAt) : NaN;
  if (!Number.isFinite(ultimo)) return "sin-latidos";
  return ahora - ultimo > SIN_SENAL_MS ? "sin-senal" : "conectada";
}

/** Si el mandato de la caja sigue vigente; `null` si la lista no lo dice. */
export function mandatoVigente(
  caja: Pick<CajaDelNegocio, "mandate">,
  ahora: number
): boolean | null {
  if (caja.mandate === undefined) return null;
  return caja.mandate !== null && ahora < Date.parse(caja.mandate.hardUntil);
}

/**
 * Las cajas que pueden tener ventas sin subir (D16; §2.3): sin señal, y con un
 * mandato vigente, o sea que todavía pueden vender sin red. Un core de la ronda
 * 2 dice el vencimiento del mandato; uno de la ronda 1 no, y como el mandato se
 * renueva con cada latido, se cuentan 72 h desde el último.
 */
export function cajasConVentasSinSubir(
  cajas: CajaDelNegocio[],
  ahora: number
): CajaDelNegocio[] {
  return cajas.filter((caja) => {
    if (senalDeLaCaja(caja, ahora) !== "sin-senal") return false;
    const vigente = mandatoVigente(caja, ahora);
    if (vigente !== null) return vigente;
    return (
      ahora - Date.parse(caja.lastHeartbeatAt as string) <
      VIGENCIA_DEL_MANDATO_MS
    );
  });
}

/** Lo que dice el mandato de la caja, para el dueño; `null` si no se sabe. */
export function textoDelMandato(
  caja: Pick<CajaDelNegocio, "mandate">,
  ahora: number
): string | null {
  if (caja.mandate === undefined) return null;
  if (caja.mandate === null) return "Sin mandato: no vende sin conexión";
  return mandatoVigente(caja, ahora)
    ? `Vende sin conexión hasta el ${hora(caja.mandate.hardUntil)}`
    : `El mandato venció el ${hora(caja.mandate.hardUntil)}`;
}

function enumerar(nombres: string[]): string {
  return nombres.length < 2
    ? nombres.join("")
    : `${nombres.slice(0, -1).join(", ")} y ${nombres[nombres.length - 1]}`;
}

/**
 * Lo que ve quien cierra la caja del local (D16): se puede cerrar igual, y lo
 * que suba después entra en la caja que esté abierta, con `CASH_DESK_CLOSED`.
 */
export function avisoDelCierre(cajas: CajaDelNegocio[]): string | null {
  if (cajas.length === 0) return null;
  const quienes =
    cajas.length === 1
      ? `«${cajas[0].name}» no tiene conexión desde el ${hora(
          cajas[0].lastHeartbeatAt as string
        )} y puede tener ventas sin subir.`
      : `${enumerar(
          cajas.map((caja) => `«${caja.name}»`)
        )} no tienen conexión y pueden tener ventas sin subir.`;
  return `${quienes} Podés cerrar igual: lo que suba después entra en la caja que esté abierta, con una observación.`;
}
